import { StreamingConstants } from "./StreamingConstants";

interface User {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  batchId: number;
  programId: number;
  role_id: number | null;
  session_id: number | null;
  created_on: string;
  programme_rating: number | null;
  emailSent: boolean;
  regStatus: boolean;
  isAnonymous: boolean;
  profilePhoto: string;
}

export function getProtooUrl({
  roomId,
  peerId,
}: {
  roomId: String;
  peerId: String;
}) {
  return (
    StreamingConstants.STREAMING_SERVER_URL +
    `/protoo/?roomId=${roomId}&peerId=${peerId}`
  );
}

export const parseToJson: any = (data: string) => {
  if (typeof data === typeof '') {
    try {
      return parseToJson(JSON.parse(data));
    } catch (err) {
      console.log('Error parsing JSON', data)
      return {}
    }
  } else {
    return data;
  }
}

export const getCamState = (videoProducer, canSendWebcam, allowed = true) => {
  if (videoProducer && videoProducer.type !== 'share') {
    return 'on';
  }
  if (!allowed) {
    return 'denied';
  }
  if (!canSendWebcam) {
    return 'unsupported';
  }
  return 'off';
};

export const userToParticipantConverter = (user: { id: number; participant_id: number; name: string; participant_name: string; email: string; participant_email: string; }) => {
  if (user.hasOwnProperty('id')) {
    user['participant_id'] = user.id;
  }
  if (user.hasOwnProperty('name')) {
    user['participant_name'] = user.name;
  }
  if (user.hasOwnProperty('email')) {
    user['participant_email'] = user.email;
  }
  console.log('returning user', user)
  return user;
}

export function getUserByEmail(email: string, users: User[]): User | undefined {
  return users.find((user) => user.email === email);
}

export const findParticipantByEmail = (data: any, email: string) => {
  const participant = data?.batch?.participants.find((participant: any) => {
    return (
      participant.email.toString().toLocaleLowerCase() ==
      email.toLocaleLowerCase()
    );
  });

  return participant || null;
};

export const getMicState = (audioProducer, canSendMic, allowed = true) => {
  // из-за вот этого кейса: https://granatum.atlassian.net/browse/TASK-4179
  // нам пришлось расположить эту проверку в самом верху
  if (audioProducer && !audioProducer.paused) {
    return 'on';
  }
  if (!allowed) {
    return 'denied';
  }
  if (!canSendMic || !audioProducer) {
    return 'unsupported';
  }
  return 'off';
};

export const getGroupName = (groupName: string) => {
  // console.log('groupName::::', groupName)
  let grpName = '';
  if (groupName && typeof groupName == typeof '') {
    const seq = groupName?.split(' ');
    if (seq.length > 1) {
      grpName = 'Group ' + seq[1]
    }
  }
  return grpName;
}

export const isBlankOrNull = (value: string) => {
  return !value || value?.length === 0 || value === 'null' || value === 'undefined';
}

export const groupBy = (objectArray: any[], property: string) => {
  return objectArray.reduce(function (acc, obj) {
    var key = obj[property];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
}

// Convert the hook to a plain function (place this outside your component, e.g., in a utils file or at the top of the component file)
export const calculatePrivateRoomMuteStates = ({
  peerIds,
  profileData,
  rpUsers = [],
  trainerEmail = "trainer@vtt",
  currentDebriefedEmail,
}: any) => {
  const muteStates: Record<string, boolean> = {};

  const myEmail = profileData.email;
  const isTrainer = myEmail === trainerEmail;
  const isRP = rpUsers.some(u => u.userEmail === myEmail);
  const isDebriefedRP = myEmail === currentDebriefedEmail;
  const isOtherRP = isRP && !isDebriefedRP;
  const isParticipant = !isTrainer && !isRP;

  // Helper: find if a peerId is an RP, Debriefed RP, Trainer, or Other Participant
  const getRole = (email: string) => {
    if (email === trainerEmail) return "trainer";
    if (email === currentDebriefedEmail) return "debriefedRP";
    if (rpUsers.some(u => u.userEmail === email)) return "otherRP";
    return "participant";
  };

  peerIds.forEach((peerEmail) => {
    // skip self
    if (peerEmail === myEmail) {
      muteStates[peerEmail] = true;
      return;
    }

    const peerRole = getRole(peerEmail);
    let mute = true; // default: muted

    // Apply rules for non-trainer listeners
    if (isDebriefedRP) {
      mute = peerRole !== "trainer";
    }
    else if (isOtherRP) {
      mute = true;
    }
    else if (isParticipant) {
      // participants can hear only other participants
      mute = peerRole !== "participant";
    }

    muteStates[peerEmail] = mute;
  });

  return { muteStates };
};

export const calculateStageThreeMuteStates = ({
  peerIds,
  profileData,
  rpUsers = [],
  trainerEmail = "trainer@vtt",
  tables = []
}: any) => {
  const muteStates: Record<string, boolean> = {};

  const myEmail = profileData.email;
  const isTrainer = myEmail === trainerEmail;
  const isRoleplayer = rpUsers.some((u) => u.userEmail === myEmail);
  const isParticipant = !isTrainer && !isRoleplayer;
  const roleplayerEmails = rpUsers.map((u) => u.userEmail);

  // Find the current user's table
  const myTable = tables.find((t: any) =>
    t.seats.some((s: any) => s.participant_email === myEmail)
  );
  const myTableEmails = myTable
    ? myTable.seats.map((s: any) => s.participant_email)
    : [];

  peerIds.forEach((peerEmail) => {
    // Default mute: true
    let mute = true;

    // Self should always be muted (don’t hear yourself)
    if (peerEmail === myEmail) {
      muteStates[peerEmail] = true;
      return;
    }

    const peerIsTrainer = peerEmail === trainerEmail;
    const peerIsRoleplayer = roleplayerEmails.includes(peerEmail);

    if (isTrainer) {
      // 🧑‍🏫 Trainer hears everyone
      mute = false;
    }
    else if (isRoleplayer) {
      // 🎭 Roleplayer hears Trainer + Other Roleplayers
      mute = !(peerIsTrainer || peerIsRoleplayer);
    }
    else if (isParticipant) {
      // 👤 Participant hears Trainer + Roleplayers + same table participants
      const peerInSameTable = myTableEmails.includes(peerEmail);
      mute = !(peerIsTrainer || peerIsRoleplayer || peerInSameTable);
    }

    muteStates[peerEmail] = mute;
  });

  return { muteStates };
};


export const updateAudioByTable = (
  tables,
  myEmail,
  trainerPeer,
  trainerJoinedtable,
  peerIds
) => {
  const muteStates: Record<string, boolean> = {};

  // 1️⃣ Find my table
  const myTable = tables.find(table =>
    table.seats.some(seat => seat.participant_email === myEmail)
  );

  if (!myTable) {
    console.warn("User not found in any table:", myEmail);
    return { muteStates };
  }

  const myGroupName = myTable.table_name;
  const trainerJoinedGroup = trainerJoinedtable?.groupName || null;
  const trainerPresent = trainerJoinedtable?.isTrainerPresent;

  // 2️⃣ Iterate all peers and determine who I can hear
  peerIds.forEach(peerEmail => {
    // Default: muted
    let canHear = false;

    // Case 1 — Trainer logic
    if (peerEmail === trainerPeer) {
      if (trainerPresent) {
        // Trainer has joined a group
        if (trainerJoinedGroup === myGroupName) {
          // I’m in the same group — can hear trainer
          canHear = true;
        } else {
          // Trainer joined another table — can't hear
          canHear = false;
        }
      } else {
        // Trainer not joined any group — everyone can hear trainer
        canHear = true;
      }
    } else {
      // Case 2 — Participant logic
      const peerTable = tables.find(table =>
        table.seats.some(seat => seat.participant_email === peerEmail)
      );

      if (peerTable) {
        if (peerTable.table_name === myGroupName) {
          // same table => can hear
          canHear = true;
        } else {
          // different table
          canHear = false;
        }
      }
    }

    muteStates[peerEmail] = !canHear; // true => muted, false => unmuted
  });

  console.log("Mute states: bye", muteStates);
  return { muteStates };
};

export const updateGdAudioByTable = (
  tables,
  myEmail,
  trainerPeer,
  peerIds
) => {
  const muteStates: Record<string, boolean> = {};

  // 1️⃣ Find my table
  const myTable = tables.find(table =>
    table.seats.some(seat => seat.participant_email === myEmail)
  );

  if (!myTable) {
    console.warn("User not found in any table:", myEmail);
    return { muteStates };
  }

  const trainerJoinedtable = tables.find(table => table.seats.some(seat => seat.participant_email === 'trainer@vtt'));
  console.log('trainerJoinedtable bye', trainerJoinedtable);
  

  const myGroupName = myTable?.table_name || myTable?.groupName;
  const trainerJoinedGroup = trainerJoinedtable?.groupName || trainerJoinedtable?.table_name || null;
  const trainerPresent = trainerJoinedtable ? true : false;

  // 2️⃣ Iterate all peers and determine who I can hear
  peerIds.forEach(peerEmail => {
    // Default: muted
    let canHear = false;

    // Case 1 — Trainer logic
    if (peerEmail === trainerPeer) {
      if (trainerPresent) {
        // Trainer has joined a group
        if (trainerJoinedGroup === myGroupName) {
          // I’m in the same group — can hear trainer
          canHear = true;
        } else {
          // Trainer joined another table — can't hear
          canHear = false;
        }
      } else {
        // Trainer not joined any group — everyone can hear trainer
        canHear = true;
      }
    } else {
      // Case 2 — Participant logic
      const peerTable = tables.find(table =>
        table.seats.some(seat => seat.participant_email === peerEmail)
      );

      if (peerTable) {
        if (peerTable.table_name === myGroupName) {
          // same table => can hear
          canHear = true;
        } else {
          // different table
          canHear = false;
        }
      }
    }

    muteStates[peerEmail] = !canHear; // true => muted, false => unmuted
  });

  console.log("Mute states: bye", muteStates);
  return { muteStates };
};

export const setCookie = (name: any, value: any, hours = 24) => {
  const date = new Date();
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);

 const wrappedValue = JSON.stringify({
    token: value,
    target: "learner-live-v3.viliyo.com"
  });

  document.cookie = `${name}=${wrappedValue}; expires=${date.toUTCString()}; path=/; domain=.viliyo.com; SameSite=None; Secure`;
};






